/* eslint-disable no-param-reassign */

import Vue from 'vue';
import Vuex from 'vuex';

import { exportDB, importInto } from 'dexie-export-import';

import db from './db';

Vue.use(Vuex);

function uuid() {
  return Math.random().toString(36).substr(2, 9);
}

export default new Vuex.Store({
  state: {
    application: {
      emotions: {
        angry: ['#FF7E7E', '#EB7070'],
        annoyed: ['#FF7E7E', '#EB7070'],
        blank: ['#7ED8FF', '#71C8EE'],
        blushing: ['#FF7E9D', '#EA6F85'],
        cool: ['#FFF27E', '#FFDD7E'],
        happy: ['#FFF27E', '#FFDD7E'],
        sad: ['#7ED8FF', '#71C8EE'],
        sick: ['#97F077', '#7EDE5C'],
        sleeping: ['#A7EFFF', '#99E0F0'],
        'slightly-annoyed': ['#FF7E7E', '#EB7070'],
        smart: ['#B77EFF', '#A269EA'],
        smile: ['#FFF27E', '#FFDD7E'],
        sobbing: ['#7ED8FF', '#71C8EE'],
        unhappy: ['#7ED8FF', '#71C8EE'],
      },
      installPrompt: null,
      stateInitialized: false,
      toasts: [],
    },
    user: {
      forceChangelog: false,
      name: 'Anon',
      onboardingComplete: false,
      prompted: false,
    },
  },
  mutations: {
    addToast(state, toast) {
      if (!toast.id) toast.id = uuid();
      if (!toast.timeout) toast.timeout = 5000;
      state.application.toasts.push(toast);
    },
    clearToasts(state) {
      state.application.toasts = [];
    },
    removeToast(state, id) {
      const index = state.application.toasts.findIndex((toast) => toast.id === id);
      if (index >= 0) state.application.toasts.splice(index, 1);
    },
    setForceChangelog(state, value) {
      state.user.forceChangelog = value;
    },
    setInstallPrompt(state, prompt) {
      state.application.installPrompt = prompt;
    },
    setName(state, value) {
      state.user.name = `${value[0].toUpperCase()}${value.substr(1).trim()}`;
    },
    setOnboardingComplete(state, value) {
      state.user.onboardingComplete = value;
    },
    setStateInitialized(state, value) {
      state.application.stateInitialized = value;
    },
    setUserPrompted(state, value) {
      state.user.prompted = value;
    },
  },
  actions: {
    async exportDB({ commit }, progressCallback) {
      try {
        const blob = await exportDB(db, { prettyJson: true, progressCallback });
        return blob;
      } catch (err) {
        commit('addToast', { message: `Could not export database: ${err.message}` });
        return false;
      }
    },
    async importDB({ commit, dispatch }, { file, progressCallback }) {
      try {
        await importInto(db, file, { clearTablesBeforeImport: true, progressCallback });
        await dispatch('loadDataFromDB');
        return true;
      } catch (err) {
        commit('addToast', { message: `Could not import database: ${err.message}` });
        return false;
      }
    },
    async loadDataFromDB({ commit }) {
      try {
        const user = await db.user.get('user');
        if (user && user.onboardingComplete) commit('setOnboardingComplete', user.onboardingComplete);
        if (user && user.name) commit('setName', user.name);
        if (user && user.forceChangelog) commit('setForceChangelog', user.forceChangelog);
        if (user && user.prompted) commit('prompted', user.prompted);
        commit('setStateInitialized', true);
      } catch (err) {
        commit('addToast', { message: `There was an error loading the data from the database: ${err.message}` });
      }
    },
    async saveUser({ commit, state }) {
      try {
        return await db.user.put(state.user, 'user');
      } catch (err) {
        return commit('addToast', { message: `There was an error saving the user data: ${err.message}` });
      }
    },
    setForceChangelog({ commit, dispatch }, value) {
      commit('setForceChangelog', value);
      dispatch('saveUser');
    },
    setOnboardingComplete({ commit, dispatch }, value) {
      commit('setOnboardingComplete', value);
      return dispatch('saveUser');
    },
    setName({ commit, dispatch }, value) {
      commit('setName', value);
      return dispatch('saveUser');
    },
    setUserPrompted({ commit, dispatch }, value) {
      commit('setUserPrompted', value);
      dispatch('saveUser');
    },
  },
});
